import React from "react";
import { SynonymEditableComponent, SynonymChipGroup } from "../../components";
import {
  VirtualFolderContextData,
  useColorProvider,
  useMessageBar,
  VirtualFolderAction,
  VirtualFolderActionType,
  SynonymsApi,
  errorToStr,
} from "../../helpers";
import { EditableSynonymGroup, EditableSynonymSubgroup } from "../../types";
import { SearchKeywordHelper } from "../SearchPage/helpers/search-keyword-helper";

interface Props {
  context: VirtualFolderContextData;
  dispatch: React.Dispatch<VirtualFolderAction> | undefined;
}

/** 仮想フォルダー同義語編集可能コンポーネントラッパー */
export function VirtualFolderSynonymEditableWrapper(props: Props) {
  const getColor = useColorProvider();
  const enqueueMessge = useMessageBar();

  const { context, dispatch } = props;
  const {
    editableSearchKeywords,
    synonym: { updateStatus, lastUpdateInfo },
  } = context;

  // 同義語の編集が適用されたら更新APIを発行する
  React.useEffect(() => {
    // 画面を切り替えても処理を継続するため
    // cleanupの制御は行わない
    if (updateStatus === "waiting") {
      /**
       * 実行待ち
       */
      if (dispatch) {
        dispatch({
          type: VirtualFolderActionType.UPDATE,
          payload: {
            ...context,
            synonym: {
              updateStatus: "doing",
              lastUpdateInfo: {
                beginDate: new Date(),
              },
            },
          },
        });
      }

      // DB更新データを取得する
      const dbData = SearchKeywordHelper.getSynonymUpdateDbData(
        editableSearchKeywords
      );
      // console.log("★ onApply dbData:", dbData);

      // 同義語更新APIを呼び出す
      SynonymsApi.update_synonyms_data({ synonyms_data: dbData })
        .then(() => {
          // 追加同義語のIDを更新する（再取得）
          // 追加された後に削除されている同義語を除去する
          // 成功を通知する
          enqueueMessge(`同義語設定を更新しました。`, "success");
          if (dispatch) {
            dispatch({
              type: VirtualFolderActionType.UPDATE,
              payload: {
                ...context,
                synonym: {
                  updateStatus: "done",
                  lastUpdateInfo: {
                    ...lastUpdateInfo,
                    result: "succeeded" as "succeeded",
                    endDate: new Date(),
                  },
                },
              },
            });
          }
          return true;
        })
        .catch(error => {
          enqueueMessge(
            `同義語設定の更新に失敗しました。 ${errorToStr(error)}`,
            "error"
          );
          if (dispatch) {
            dispatch({
              type: VirtualFolderActionType.UPDATE,
              payload: {
                ...context,
                synonym: {
                  updateStatus: "done",
                  lastUpdateInfo: {
                    ...lastUpdateInfo,
                    result: "failed" as "failed",
                    error,
                    endDate: new Date(),
                  },
                },
              },
            });
          }
          return false;
        });
    } else if (updateStatus === "doing") {
      /**
       * 更新中(API実行待ち)
       */
      // 何もしない
    }
  }, [updateStatus]);

  // 同義語の編集状態をすべてクリアする
  const handleClear = () => {
    if (dispatch) {
      // 同義語サブグループの状態を更新する
      dispatch({
        type: VirtualFolderActionType.UPDATE,
        payload: {
          ...context,
          editableSearchKeywords: SearchKeywordHelper.resetEditedSynonyms(
            context.editableSearchKeywords
          ),
        },
      });
    }
  };

  // 同義語の編集状態を適用する
  const handleApply = () => {
    if (dispatch) {
      dispatch({
        type: VirtualFolderActionType.UPDATE,
        payload: {
          ...context,
          synonym: {
            updateStatus: "waiting",
          },
        },
      });
    }
  };

  // 同義語を展開または折りたたむ
  const handleExpandSynonyms = (
    target: EditableSynonymGroup,
    expand: boolean
  ) => {
    if (dispatch) {
      dispatch({
        type: VirtualFolderActionType.UPDATE,
        payload: {
          ...context,
          editableSearchKeywords: SearchKeywordHelper.updateGroup(
            editableSearchKeywords,
            target,
            expand,
            target.editText,
            target.editing
          ),
        },
      });
    }
  };

  // 同義語編集可能コンポーネントの操作モードを取得する
  const controlMode = SearchKeywordHelper.getSynonymEditableComponentControlMode(
    editableSearchKeywords,
    updateStatus,
    lastUpdateInfo?.result
  );

  // 通常高さ: 32px, スクロールバー表示時高さ: 43px
  let component = <div style={{ height: "43px" }} />;
  if (editableSearchKeywords.length > 0) {
    component = (
      <SynonymEditableComponent
        mode={controlMode}
        onClear={handleClear}
        onApply={handleApply}
      >
        {/* 同義語グループ */}
        {editableSearchKeywords.map(
          (synonymGroup: EditableSynonymGroup, index: number) => {
            return (
              <SynonymChipGroup
                key={synonymGroup.baseword}
                group={synonymGroup}
                disabled={["disabled", "doing", "done"].includes(controlMode)}
                color={getColor(index)}
                onExpandSynonyms={(target: EditableSynonymGroup) => {
                  handleExpandSynonyms(target, true);
                }}
                onShrinkSynonyms={(target: EditableSynonymGroup) => {
                  handleExpandSynonyms(target, false);
                }}
                onChangeEditText={(
                  target: EditableSynonymGroup,
                  newText: string
                ) => {
                  if (dispatch) {
                    dispatch({
                      type: VirtualFolderActionType.UPDATE,
                      payload: {
                        ...context,
                        editableSearchKeywords: SearchKeywordHelper.updateGroup(
                          context.editableSearchKeywords,
                          target,
                          target.expand,
                          newText,
                          target.editing
                        ),
                      },
                    });
                  }
                }}
                onAddingSynonym={(group: EditableSynonymGroup) => {
                  if (dispatch) {
                    dispatch({
                      type: VirtualFolderActionType.UPDATE,
                      payload: {
                        ...context,
                        editableSearchKeywords: SearchKeywordHelper.updateAdding(
                          context.editableSearchKeywords,
                          group
                        ),
                      },
                    });
                  }
                }}
                onChangeDeletingSynonym={(
                  editableSubgroup: EditableSynonymSubgroup,
                  deleting: boolean
                ) => {
                  if (dispatch) {
                    dispatch({
                      type: VirtualFolderActionType.UPDATE,
                      payload: {
                        ...context,
                        editableSearchKeywords: SearchKeywordHelper.updateDeleting(
                          context.editableSearchKeywords,
                          editableSubgroup,
                          deleting
                        ),
                      },
                    });
                  }
                }}
                onEditing={(target: EditableSynonymGroup) => {
                  if (dispatch) {
                    dispatch({
                      type: VirtualFolderActionType.UPDATE,
                      payload: {
                        ...context,
                        editableSearchKeywords: SearchKeywordHelper.updateGroup(
                          context.editableSearchKeywords,
                          target,
                          target.expand,
                          target.editText,
                          true
                        ),
                      },
                    });
                  }
                }}
                onCancelEdit={(target: EditableSynonymGroup) => {
                  if (dispatch) {
                    dispatch({
                      type: VirtualFolderActionType.UPDATE,
                      payload: {
                        ...context,
                        editableSearchKeywords: SearchKeywordHelper.updateGroup(
                          context.editableSearchKeywords,
                          target,
                          target.expand,
                          "",
                          false
                        ),
                      },
                    });
                  }
                }}
              />
            );
          }
        )}
      </SynonymEditableComponent>
    );
  }

  return component;
}
